/**
 * UniverseChips — the small per-row market-cap + country chips shown on filtered stock lists, so a user
 * can see at a glance why a name is (or is not) in their universe. Presentational + pure: pass the two
 * fields off a SymbolMeta. Used on the feed, dashboards, and scanners. Country only renders when it is a
 * readable name (never a raw EDGAR code — see isDisplayableCountry); China/HK get a faint amber tint so
 * they stand out when the "Exclude China + HK" filter is off.
 */
import { isDisplayableCountry } from '@/lib/filters/universeFilter'

function fmtCap(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return 'cap n/a'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(0)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
  return `$${Math.round(n / 1e3)}K`
}

export function UniverseChips({
  marketCap,
  country,
  className = '',
}: {
  marketCap: number | null | undefined
  country?: string | null
  className?: string
}) {
  const hasCap = marketCap != null && Number.isFinite(marketCap) && marketCap > 0
  const showCountry = isDisplayableCountry(country)
  const isCn = showCountry && (country === 'China' || country === 'Hong Kong')
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      <span
        className={`rounded px-1.5 py-0.5 text-[9.5px] font-bold ${
          hasCap
            ? 'border border-sky-500/25 bg-sky-500/10 text-sky-200'
            : 'border border-zinc-700 bg-white/[.03] text-zinc-500'
        }`}
      >
        {hasCap ? `${fmtCap(marketCap)} cap` : 'cap n/a'}
      </span>
      {showCountry && (
        <span
          className={`rounded px-1.5 py-0.5 text-[9.5px] font-bold ${
            isCn
              ? 'border border-amber-500/30 bg-amber-500/10 text-amber-200'
              : 'border border-zinc-700 bg-white/[.03] text-zinc-400'
          }`}
        >
          {country}
        </span>
      )}
    </span>
  )
}
